import { format } from 'date-fns';
import { db } from './firebase';
import { firestoreKeys } from '../utils/constants';

export default function useMailService() {
  const sendEmail = (data) => {
    try {
      if (!data.to || !data.message) {
        console.error('Missing required email fields:', { to: data.to, hasMessage: !!data.message });
        throw new Error('Missing required email fields');
      }

      db.collection(firestoreKeys.MAIL).add(data);
    } catch (error) {
      console.error('Error sending email: ', error);
      throw error;
    }
  };

  // Sends a review request email to a recipient
  const sendReviewRequestEmail = async ({ to, senderFirstName, senderLastName, listingTitle, listingId, origin }) => {
    const html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Stintz Email</title>
          <style>
              body {
                  font-family: Arial, sans-serif;
                  margin: 0;
                  padding: 0;
                  background-color: #f4f4f4;
              }
               .content {
                   max-width: 480px;
                   margin: 40px auto;
                   background: #fff;
                   padding: 32px 32px 40px 32px;
                   border-radius: 12px;
                   box-shadow: 0 4px 24px rgba(0,0,0,0.07);
                   text-align: center;
                   font-family: 'Public Sans', Arial, sans-serif;
               }
              .content h1 {
                  text-align: center;
              }
              .content a {
                  display: inline-block;
                  padding: 10px 20px;
                  margin: 20px 0;
                  background-color: #3366FF;
                  color: #ffffff;
                  text-decoration: none;
                  text-align: center;
                  border-radius: 5px;
                  font-weight: 600;
                  font-size: 16px;
                  letter-spacing: 0.5px;
                  box-shadow: 0 2px 8px rgba(51, 102, 255, 0.10);
              }
               .content a:hover {
                  background-color: #1939B7;
              }
          </style>
      </head>
      <body style="background: #F4F6F8; margin:0; padding:0;">
          <div style="padding: 40px 0;">
              <div class="content">
                  <img src="https://stintz-dev.firebaseapp.com/static/media/newLogo.ec4a87ddae1d8c7f7faa.png" alt="Stintz Logo" style="height: 48px; margin-bottom: 24px;" />
                  <h1 style="color: #3366FF; font-size: 28px; margin-bottom: 8px;">Leave a Review</h1>
                  <h2 style="color: #212B36; font-size: 20px; margin: 0 0 20px 0; font-weight: 700;">Hi there! 👋</h2>
                  <p style="color: #454F5B; font-size: 16px; line-height: 1.7; margin-bottom: 28px;">
                      ${senderFirstName} ${senderLastName} has joined <span style="color: #3366FF; font-weight: 600;">Stintz</span> and is inviting you to leave a review for <b>${listingTitle}</b>.<br/>Your feedback helps us build a trusted community!
                  </p>
                  <a href="${origin}/external-reviews/${listingId}">Leave a Review</a>
                  <p style="color: #637381; font-size: 13px; margin-top: 32px;">Thank you for helping us make Stintz better!<br/>— The Stintz Team</p>
              </div>
          </div>
      </body>
      </html>
    `;

    const emailData = {
      message: {
        subject: `${senderFirstName} ${senderLastName} has requested a review on Stintz`,
        html,
      },
      to,
    };

    await sendEmail(emailData);
  };

  // Sends a reschedule request email to a recipient
  const sendRescheduleRequestEmail = async ({
    to,
    listingName,
    message,
    origin,
    bookingId,
    startTime,
    endTime,
    sentBy,
  }) => {
    // Format date and time for display (robust for Node/email)

    function formatDateStr(dateStr) {
      if (!dateStr) return '-';
      try {
        const d = new Date(dateStr);
        if (!Number.isNaN(d.getTime())) {
          return format(d, 'PPP');
        }
      } catch (e) {
        return '-';
      }
      return '-';
    }
    function formatTimeRange(startStr, endStr) {
      if (!startStr || !endStr) return '-';
      try {
        const start = new Date(startStr);
        const end = new Date(endStr);
        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
          return `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`;
        }
      } catch (e) {
        return '-';
      }
      return '-';
    }
    const dateLabel = formatDateStr(startTime);
    const timeRange = formatTimeRange(startTime, endTime);

    const html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Stintz Email</title>
          <style>
              body {
                  font-family: Arial, sans-serif;
                  margin: 0;
                  padding: 0;
                  background-color: #f4f4f4;
              }
               .content {
                   max-width: 480px;
                   margin: 40px auto;
                   background: #fff;
                   padding: 32px 32px 40px 32px;
                   border-radius: 12px;
                   box-shadow: 0 4px 24px rgba(0,0,0,0.07);
                   text-align: center;
                   font-family: 'Public Sans', Arial, sans-serif;
               }
              .content h1 {
                  text-align: center;
                  color: #3366FF;
                  font-size: 28px;
                  margin-bottom: 8px;
              }
              .info-box {
                  background: #F4F6F8;
                  border-radius: 8px;
                  padding: 20px 18px 18px 18px;
                  margin: 24px 0 24px 0;
                  text-align: left;
                  color: #212B36;
                  font-size: 16px;
              }
              .info-box strong {
                  color: #3366FF;
              }
              .info-box p {
                  margin: 0 0 10px 0;
                  color: #212B36;
                  font-size: 16px;
              }
              .content p {
                  color: #454F5B;
                  font-size: 16px;
                  line-height: 1.7;
                  margin-bottom: 20px;
              }
              .content a.cta {
                  display: inline-block;
                  padding: 10px 20px;
                  margin: 28px 0 0 0;
                  background-color: #3366FF;
                  color: #ffffff !important;
                  text-decoration: none;
                  text-align: center;
                  border-radius: 5px;
                  font-weight: 600;
                  font-size: 16px;
                  letter-spacing: 0.5px;
                  box-shadow: 0 2px 8px rgba(51, 102, 255, 0.10);
                  transition: background 0.2s;
              }
              .content a.cta:hover {
                  background-color: #1939B7;
              }
          </style>
      </head>
      <body style="background: #F4F6F8; margin:0; padding:0;">
          <div style="padding: 40px 0;">
              <div class="content">
                  <img src="https://stintz-dev.firebaseapp.com/static/media/newLogo.ec4a87ddae1d8c7f7faa.png" alt="Stintz Logo" style="height: 48px; margin-bottom: 24px;" />
                  <h1>Reschedule Request</h1>
                  <p>A reschedule request has been sent for the booking: <strong>${listingName}</strong>.</p>
                  <div class="info-box">
                    <p><strong>Date:</strong> ${dateLabel || '-'}</p>
                    <p><strong>Time:</strong> ${timeRange}</p>
                    <p>${message || ''}</p>
                  </div>
                  <a href="${origin}/${
      sentBy === 'provider' ? `customer/bookings/${bookingId}/details` : `provider/bookings/${bookingId}/details`
    }" class="cta">View Booking Details</a>
                  <p style="color: #637381; font-size: 13px; margin-top: 32px;">Thank you for helping us make Stintz better!<br/>— The Stintz Team</p>
              </div>
          </div>
      </body>
      </html>
    `;
    const emailData = {
      message: {
        subject: `Reschedule Request for ${listingName}`,
        html,
      },
      to,
    };
    await sendEmail(emailData);
  };

  // Sends an OTP for review verification email to a recipient
  const sendOTPForReviewVerificationEmail = async ({ to, otp }) => {
    const html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Stintz Email</title>
          <style>
              body {
                  font-family: Arial, sans-serif;
                  margin: 0;
                  padding: 0;
                  background-color: #f4f4f4;
              }
               .content {
                   max-width: 480px;
                   margin: 40px auto;
                   background: #fff;
                   padding: 32px 32px 40px 32px;
                   border-radius: 12px;
                   box-shadow: 0 4px 24px rgba(0,0,0,0.07);
                   text-align: center;
                   font-family: 'Public Sans', Arial, sans-serif;
               }
              .content h1 {
                  text-align: center;
                  color: #3366FF;
                  font-size: 28px;
                  margin-bottom: 8px;
              }
              .otp-box {
                  background: #F4F6F8;
                  border-radius: 8px;
                  padding: 20px 18px 18px 18px;
                  margin: 24px 0 24px 0;
                  text-align: center;
                  color: #212B36;
                  font-size: 24px;
                  font-weight: bold;
                  letter-spacing: 4px;
              }
              .content p {
                  color: #454F5B;
                  font-size: 16px;
                  line-height: 1.7;
                  margin-bottom: 20px;
              }
          </style>
      </head>
      <body style="background: #F4F6F8; margin:0; padding:0;">
          <div style="padding: 40px 0;">
              <div class="content">
                  <img src="https://stintz-dev.firebaseapp.com/static/media/newLogo.ec4a87ddae1d8c7f7faa.png" alt="Stintz Logo" style="height: 48px; margin-bottom: 24px;" />
                  <h1>Review Verification</h1>
                  <p>Use the following One-Time Password (OTP) to verify your review submission:</p>
                  <div class="otp-box">${otp}</div>
                  <p style="color: #637381; font-size: 13px; margin-top: 32px;">Thank you for helping us make Stintz better!<br/>— The Stintz Team</p>
              </div>
          </div>
      </body>
      </html>
    `;

    const emailData = {
      message: {
        subject: 'OTP for Review Verification',
        html,
      },
      to,
    };

    await sendEmail(emailData);
  };

  // Sends a quote email to a recipient
  const sendQuoteEmail = async ({ to, senderFirstName, senderLastName, listingTitle, quoteId, origin }) => {
    const html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Stintz Email</title>
          <style>
              body {
                  font-family: Arial, sans-serif;
                  margin: 0;
                  padding: 0;
                  background-color: #f4f4f4;
              }
              .content {
                  max-width: 480px;
                  margin: 40px auto;
                  background: #fff;
                  padding: 32px 32px 40px 32px;
                  border-radius: 12px;
                  box-shadow: 0 4px 24px rgba(0,0,0,0.07);
                  text-align: center;
                  font-family: 'Public Sans', Arial, sans-serif;
              }
              .content h1 {
                  text-align: center;
                  color: #3366FF;
                  font-size: 28px;
                  margin-bottom: 8px;
              }
              .content p {
                  color: #454F5B;
                  font-size: 16px;
                  line-height: 1.7;
                  margin-bottom: 28px;
              }
              .content a {
                  display: inline-block;
                  padding: 10px 20px;
                  margin: 20px 0;
                  background-color: #3366FF;
                  color: #ffffff;
                  text-decoration: none;
                  text-align: center;
                  border-radius: 5px;
                  font-weight: 600;
                  font-size: 16px;
                  letter-spacing: 0.5px;
                  box-shadow: 0 2px 8px rgba(51, 102, 255, 0.10);
              }
              .content a:hover {
                  background-color: #1939B7;
              }
          </style>
      </head>
      <body style="background: #F4F6F8; margin:0; padding:0;">
          <div style="padding: 40px 0;">
              <div class="content">
                  <img src="https://stintz-dev.firebaseapp.com/static/media/newLogo.ec4a87ddae1d8c7f7faa.png" alt="Stintz Logo" style="height: 48px; margin-bottom: 24px;" />
                  <h1 style="color: #3366FF; font-size: 28px; margin-bottom: 8px;">Your Quote is Ready</h1>
                  <h2 style="color: #212B36; font-size: 20px; margin: 0 0 20px 0; font-weight: 700;">Hi there! 👋</h2>
                  <p style="color: #454F5B; font-size: 16px; line-height: 1.7; margin-bottom: 28px;">
                      ${senderFirstName} ${senderLastName} has sent you a quote for <b>${listingTitle}</b>. Log in to the platform accept or reject the quote.
                  </p>
                  <a href="${origin}/quotes/${quoteId}/details">View Quote</a>
                  <p style="color: #637381; font-size: 13px; margin-top: 32px;">Thank you for choosing Stintz!<br/>— The Stintz Team</p>
              </div>
          </div>
      </body>
      </html>
    `;

    const emailData = {
      message: {
        subject: `${senderFirstName} ${senderLastName} has sent you a quote`,
        html,
      },
      to,
    };

    await sendEmail(emailData);
  };

  // Sends a quote rejection email to a recipient
  const sendQuoteRejectionEmail = async ({ to, customerName, listingTitle }) => {
    const html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Stintz Email</title>
          <style>
              body {
                  font-family: Arial, sans-serif;
                  margin: 0;
                  padding: 0;
                  background-color: #f4f4f4;
              }
              .content {
                  max-width: 480px;
                  margin: 40px auto;
                  background: #fff;
                  padding: 32px 32px 40px 32px;
                  border-radius: 12px;
                  box-shadow: 0 4px 24px rgba(0,0,0,0.07);
                  text-align: center;
                  font-family: 'Public Sans', Arial, sans-serif;
              }
              .content h1 {
                  text-align: center;
                  color: #FF3D00;
                  font-size: 28px;
                  margin-bottom: 8px;
              }
              .content p {
                  color: #454F5B;
                  font-size: 16px;
                  line-height: 1.7;
                  margin-bottom: 28px;
              }
              .content a {
                  display: inline-block;
                  padding: 10px 20px;
                  margin: 20px 0;
                  background-color: #FF3D00;
                  color: #ffffff;
                  text-decoration: none;
                  text-align: center;
                  border-radius: 5px;
                  font-weight: 600;
                  font-size: 16px;
                  letter-spacing: 0.5px;
                  box-shadow: 0 2px 8px rgba(255, 61, 0, 0.10);
              }
              .content a:hover {
                  background-color: #B71C1C;
              }
          </style>
      </head>
      <body style="background: #F4F6F8; margin:0; padding:0;">
          <div style="padding: 40px 0;">
              <div class="content">
                  <img src="https://stintz-dev.firebaseapp.com/static/media/newLogo.ec4a87ddae1d8c7f7faa.png" alt="Stintz Logo" style="height: 48px; margin-bottom: 24px;" />
                  <h1 style="color: #FF3D00; font-size: 28px; margin-bottom: 8px;">Quote Rejected!</h1>
                  <p style="color: #454F5B; font-size: 16px; line-height: 1.7; margin-bottom: 28px;">
                      Unfortunately, the quote for <b>${listingTitle}</b> has been rejected by ${customerName}.
                  </p>
                  <p style="color: #637381; font-size: 13px; margin-top: 32px;">Thank you for using Stintz!<br/>— The Stintz Team</p>
              </div>
          </div>
      </body>
      </html>
    `;

    const emailData = {
      message: {
        subject: `Quote Rejected for ${listingTitle}`,
        html,
      },
      to,
    };

    await sendEmail(emailData);
  };

  const sendQuoteAcceptanceEmail = async ({ to, customerName, listingTitle }) => {
    const html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Stintz Email</title>
          <style>
              body {
                  font-family: Arial, sans-serif;
                  margin: 0;
                  padding: 0;
                  background-color: #f4f4f4;
              }
              .content {
                  max-width: 480px;
                  margin: 40px auto;
                  background: #fff;
                  padding: 32px 32px 40px 32px;
                  border-radius: 12px;
                  box-shadow: 0 4px 24px rgba(0,0,0,0.07);
                  text-align: center;
                  font-family: 'Public Sans', Arial, sans-serif;
              }
              .content h1 {
                  text-align: center;
                  color: #28A745;
                  font-size: 28px;
                  margin-bottom: 8px;
              }
              .content p {
                  color: #454F5B;
                  font-size: 16px;
                  line-height: 1.7;
                  margin-bottom: 28px;
              }
              .content a {
                  display: inline-block;
                  padding: 10px 20px;
                  margin: 20px 0;
                  background-color: #28A745;
                  color: #ffffff;
                  text-decoration: none;
                  text-align: center;
                  border-radius: 5px;
                  font-weight: 600;
                  font-size: 16px;
                  letter-spacing: 0.5px;
                  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.10);
              }
              .content a:hover {
                  background-color: #1E7E34;
              }
          </style>
      </head>
      <body style="background: #F4F6F8; margin:0; padding:0;">
          <div style="padding: 40px 0;">
              <div class="content">
                  <img src="https://stintz-dev.firebaseapp.com/static/media/newLogo.ec4a87ddae1d8c7f7faa.png" alt="Stintz Logo" style="height: 48px; margin-bottom: 24px;" />
                  <h1 style="color: #28A745; font-size: 28px; margin-bottom: 8px;">Quote Accepted!</h1>
                  <p style="color: #454F5B; font-size: 16px; line-height: 1.7; margin-bottom: 28px;">
                      Great news! The quote for <b>${listingTitle}</b> has been accepted by ${customerName} and the booking has been confirmed.
                  </p>
                  <p style="color: #637381; font-size: 13px; margin-top: 32px;">Thank you for using Stintz!<br/>— The Stintz Team</p>
              </div>
          </div>
      </body>
      </html>
    `;

    const emailData = {
      message: {
        subject: `Quote Accepted for ${listingTitle}`,
        html,
      },
      to,
    };

    await sendEmail(emailData);
  };

  // Sends a payment confirmation email to the customer
  const sendPaymentConfirmationEmail = async ({
    to,
    customerName,
    listingTitle,
    bookingId,
    paymentType,
    paymentAmount,
    totalAmount,
    bookingDate,
    origin,
  }) => {
    const isFullPayment = paymentType === 'full';
    const isDepositPayment = paymentType === 'deposit';
    const isRemainingPayment = paymentType === 'remaining';

    let paymentTypeText = '';
    let colorCode = '';

    if (isFullPayment) {
      paymentTypeText = 'Full Payment';
      colorCode = '#28A745'; // Green
    } else if (isDepositPayment) {
      paymentTypeText = 'Deposit Payment';
      colorCode = '#3366FF'; // Blue
    } else if (isRemainingPayment) {
      paymentTypeText = 'Remaining Payment';
      colorCode = '#28A745'; // Green
    }

    const formattedDate = bookingDate ? format(new Date(bookingDate), 'PPP') : 'Scheduled date';

    const html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Stintz Payment Confirmation</title>
          <style>
              body {
                  font-family: Arial, sans-serif;
                  margin: 0;
                  padding: 0;
                  background-color: #f4f4f4;
              }
              .content {
                  max-width: 480px;
                  margin: 40px auto;
                  background: #fff;
                  padding: 32px 32px 40px 32px;
                  border-radius: 12px;
                  box-shadow: 0 4px 24px rgba(0,0,0,0.07);
                  text-align: center;
                  font-family: 'Public Sans', Arial, sans-serif;
              }
              .content h1 {
                  text-align: center;
                  color: ${colorCode};
                  font-size: 28px;
                  margin-bottom: 8px;
              }
              .content p {
                  color: #454F5B;
                  font-size: 16px;
                  line-height: 1.7;
                  margin-bottom: 28px;
              }
              .content a {
                  display: inline-block;
                  padding: 10px 20px;
                  margin: 20px 0;
                  background-color: ${colorCode};
                  color: #ffffff;
                  text-decoration: none;
                  text-align: center;
                  border-radius: 5px;
                  font-weight: 600;
                  font-size: 16px;
                  letter-spacing: 0.5px;
                  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.10);
              }
              .content a:hover {
                  opacity: 0.9;
              }
              .payment-details {
                  background-color: #F9FAFB;
                  border-radius: 8px;
                  padding: 16px;
                  margin: 20px 0;
                  text-align: left;
              }
              .payment-details div {
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 8px;
              }
              .payment-details .total {
                  font-weight: bold;
                  border-top: 1px solid #DFE3E8;
                  padding-top: 8px;
                  margin-top: 8px;
              }
          </style>
      </head>
      <body style="background: #F4F6F8; margin:0; padding:0;">
          <div style="padding: 40px 0;">
              <div class="content">
                  <img src="https://stintz-dev.firebaseapp.com/static/media/newLogo.ec4a87ddae1d8c7f7faa.png" alt="Stintz Logo" style="height: 48px; margin-bottom: 24px;" />
                  <h1>${paymentTypeText} Confirmed!</h1>
                  <p>
                      Hi ${customerName},<br/>
                      Your ${paymentTypeText.toLowerCase()} for <b>${listingTitle}</b> has been successfully processed.
                      ${isDepositPayment ? 'The remaining balance will be due upon completion of the service.' : ''}
                  </p>
                  
                  <div class="payment-details">
                      <div>
                          <span>Service:</span>
                          <span>${listingTitle}</span>
                      </div>
                      <div>
                          <span>Date:</span>
                          <span>${formattedDate}</span>
                      </div>
                      <div>
                          <span>Payment Type:</span>
                          <span>${paymentTypeText}</span>
                      </div>
                      <div>
                          <span>Amount Paid:</span>
                          <span>$${paymentAmount.toFixed(2)}</span>
                      </div>
                      ${
                        !isFullPayment && !isRemainingPayment
                          ? `
                      <div>
                          <span>Remaining:</span>
                          <span>$${(totalAmount - paymentAmount).toFixed(2)}</span>
                      </div>`
                          : ''
                      }
                      <div class="total">
                          <span>Total:</span>
                          <span>$${totalAmount.toFixed(2)}</span>
                      </div>
                  </div>
                  
                  <a href="${origin}/customer/bookings/${bookingId}">View Booking Details</a>
                  <p style="color: #637381; font-size: 13px; margin-top: 32px;">Thank you for using Stintz!<br/>— The Stintz Team</p>
              </div>
          </div>
      </body>
      </html>
    `;

    const emailData = {
      message: {
        subject: `${paymentTypeText} Confirmed for ${listingTitle}`,
        html,
      },
      to,
    };

    await sendEmail(emailData);
  };

  // Sends a payment notification email to the provider
  const sendProviderPaymentNotificationEmail = async ({
    to,
    providerName,
    customerName,
    listingTitle,
    bookingId,
    paymentType,
    paymentAmount,
    totalAmount,
    bookingDate,
    origin,
  }) => {
    const isFullPayment = paymentType === 'full';
    const isDepositPayment = paymentType === 'deposit';
    const isRemainingPayment = paymentType === 'remaining';

    let paymentTypeText = '';
    let colorCode = '';

    if (isFullPayment) {
      paymentTypeText = 'Full Payment';
      colorCode = '#28A745'; // Green
    } else if (isDepositPayment) {
      paymentTypeText = 'Deposit Payment';
      colorCode = '#3366FF'; // Blue
    } else if (isRemainingPayment) {
      paymentTypeText = 'Remaining Payment';
      colorCode = '#28A745'; // Green
    }

    const formattedDate = bookingDate ? format(new Date(bookingDate), 'PPP') : 'Scheduled date';

    const html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Stintz Payment Notification</title>
          <style>
              body {
                  font-family: Arial, sans-serif;
                  margin: 0;
                  padding: 0;
                  background-color: #f4f4f4;
              }
              .content {
                  max-width: 480px;
                  margin: 40px auto;
                  background: #fff;
                  padding: 32px 32px 40px 32px;
                  border-radius: 12px;
                  box-shadow: 0 4px 24px rgba(0,0,0,0.07);
                  text-align: center;
                  font-family: 'Public Sans', Arial, sans-serif;
              }
              .content h1 {
                  text-align: center;
                  color: ${colorCode};
                  font-size: 28px;
                  margin-bottom: 8px;
              }
              .content p {
                  color: #454F5B;
                  font-size: 16px;
                  line-height: 1.7;
                  margin-bottom: 28px;
              }
              .content a {
                  display: inline-block;
                  padding: 10px 20px;
                  margin: 20px 0;
                  background-color: ${colorCode};
                  color: #ffffff;
                  text-decoration: none;
                  text-align: center;
                  border-radius: 5px;
                  font-weight: 600;
                  font-size: 16px;
                  letter-spacing: 0.5px;
                  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.10);
              }
              .content a:hover {
                  opacity: 0.9;
              }
              .payment-details {
                  background-color: #F9FAFB;
                  border-radius: 8px;
                  padding: 16px;
                  margin: 20px 0;
                  text-align: left;
              }
              .payment-details div {
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 8px;
              }
              .payment-details .total {
                  font-weight: bold;
                  border-top: 1px solid #DFE3E8;
                  padding-top: 8px;
                  margin-top: 8px;
              }
          </style>
      </head>
      <body style="background: #F4F6F8; margin:0; padding:0;">
          <div style="padding: 40px 0;">
              <div class="content">
                  <img src="https://stintz-dev.firebaseapp.com/static/media/newLogo.ec4a87ddae1d8c7f7faa.png" alt="Stintz Logo" style="height: 48px; margin-bottom: 24px;" />
                  <h1>${paymentTypeText} Received!</h1>
                  <p>
                      Hi ${providerName},<br/>
                      You've received a ${paymentTypeText.toLowerCase()} from ${customerName} for <b>${listingTitle}</b>.
                      ${isDepositPayment ? 'The remaining balance will be paid upon completion of the service.' : ''}
                  </p>
                  
                  <div class="payment-details">
                      <div>
                          <span>Service:</span>
                          <span>${listingTitle}</span>
                      </div>
                      <div>
                          <span>Customer:</span>
                          <span>${customerName}</span>
                      </div>
                      <div>
                          <span>Date:</span>
                          <span>${formattedDate}</span>
                      </div>
                      <div>
                          <span>Payment Type:</span>
                          <span>${paymentTypeText}</span>
                      </div>
                      <div>
                          <span>Amount Paid:</span>
                          <span>$${paymentAmount.toFixed(2)}</span>
                      </div>
                      ${
                        !isFullPayment && !isRemainingPayment
                          ? `
                      <div>
                          <span>Remaining:</span>
                          <span>$${(totalAmount - paymentAmount).toFixed(2)}</span>
                      </div>`
                          : ''
                      }
                      <div class="total">
                          <span>Total:</span>
                          <span>$${totalAmount.toFixed(2)}</span>
                      </div>
                  </div>
                  
                  <a href="${origin}/provider/bookings/${bookingId}">View Booking Details</a>
                  <p style="color: #637381; font-size: 13px; margin-top: 32px;">Thank you for using Stintz!<br/>— The Stintz Team</p>
              </div>
          </div>
      </body>
      </html>
    `;

    const emailData = {
      message: {
        subject: `${paymentTypeText} Received for ${listingTitle}`,
        html,
      },
      to,
    };

    await sendEmail(emailData);
  };

  // Sends a support request email to the support team
  const sendSupportRequestEmail = async ({ userName, userEmail, userId, subject, description }) => {
    const html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Support Request</title>
          <style>
              body {
                  font-family: Arial, sans-serif;
                  margin: 0;
                  padding: 0;
                  background-color: #f4f4f4;
              }
              .content {
                  max-width: 600px;
                  margin: 40px auto;
                  background: #fff;
                  padding: 32px;
                  border-radius: 12px;
                  box-shadow: 0 4px 24px rgba(0,0,0,0.07);
                  font-family: 'Public Sans', Arial, sans-serif;
              }
              .header {
                  border-bottom: 2px solid #3366FF;
                  padding-bottom: 16px;
                  margin-bottom: 24px;
              }
              .field {
                  margin-bottom: 16px;
              }
              .field-label {
                  font-weight: 600;
                  color: #637381;
                  font-size: 14px;
                  margin-bottom: 4px;
              }
              .field-value {
                  color: #212B36;
                  font-size: 16px;
              }
              .description-box {
                  background: #F4F6F8;
                  padding: 16px;
                  border-radius: 8px;
                  margin-top: 8px;
              }
          </style>
      </head>
      <body style="background: #F4F6F8; margin:0; padding:0;">
          <div style="padding: 40px 0;">
              <div class="content">
                  <div class="header">
                      <img src="https://stintz-dev.firebaseapp.com/static/media/newLogo.ec4a87ddae1d8c7f7faa.png" alt="Stintz Logo" style="height: 40px;" />
                      <h1 style="color: #3366FF; font-size: 24px; margin: 16px 0 0 0;">New Support Request</h1>
                  </div>
                  
                  <div class="field">
                      <div class="field-label">From</div>
                      <div class="field-value">${userName}${userEmail ? ` (${userEmail})` : ''}</div>
                  </div>
                  
                  <div class="field">
                      <div class="field-label">User ID</div>
                      <div class="field-value">${userId}</div>
                  </div>
                  
                  <div class="field">
                      <div class="field-label">Subject</div>
                      <div class="field-value">${subject}</div>
                  </div>
                  
                  <div class="field">
                      <div class="field-label">Description</div>
                      <div class="description-box">
                          ${description}
                      </div>
                  </div>
                  
                  <p style="color: #637381; font-size: 13px; margin-top: 32px; border-top: 1px solid #E5E8EB; padding-top: 16px;">
                      This support request was submitted via the Stintz platform.
                  </p>
              </div>
          </div>
      </body>
      </html>
    `;

    const emailData = {
      message: {
        subject: `Support Request: ${subject}`,
        html,
      },
      to: 'support@stintz.com',
    };

    await sendEmail(emailData);
  };

  return {
    sendEmail,
    sendReviewRequestEmail,
    sendRescheduleRequestEmail,
    sendOTPForReviewVerificationEmail,
    sendQuoteEmail,
    sendQuoteRejectionEmail,
    sendQuoteAcceptanceEmail,
    sendPaymentConfirmationEmail,
    sendProviderPaymentNotificationEmail,
    sendSupportRequestEmail,
  };
}
